import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  useMyClockStatus,
  useMyTodayEntries,
  useClockAction,
  getEventTypeLabel,
  getEventTypeColor,
  getStatusLabel,
  getStatusColor,
} from '@/hooks/useTimeTracking';
import {
  BREAK_REASONS,
  BreakReason,
  formatMinutesToTime,
  getBreakReasonConfig,
} from '@/types/timeTracking';
import {
  Clock,
  LogIn,
  LogOut,
  Coffee,
  Play,
  Pause,
  Utensils,
  User,
  Heart,
  Wind,
  Baby,
  GraduationCap,
  Users,
  MoreHorizontal,
  Timer,
  Calendar,
  CheckCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

// Icon mapping for break reasons
const BREAK_ICONS: Record<string, any> = {
  Utensils,
  Coffee,
  User,
  Heart,
  Wind,
  Baby,
  GraduationCap,
  Users,
  MoreHorizontal,
};

export function TimeClockWidget() {
  const { toast } = useToast();
  const { userId } = useAuth();
  const { data: status, isLoading } = useMyClockStatus();
  const { data: todayEntries } = useMyTodayEntries();
  const clockAction = useClockAction();

  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [selectedBreakReason, setSelectedBreakReason] = useState<BreakReason | null>(null);
  const [breakNotes, setBreakNotes] = useState('');

  const handleClockIn = async () => {
    try {
      await clockAction.mutateAsync({
        user_id: userId!,
        event_type: 'clock_in',
      });
      toast({
        title: 'Entrada registrada',
        description: `Has fichado tu entrada a las ${format(new Date(), 'HH:mm')}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleClockOut = async () => {
    try {
      await clockAction.mutateAsync({
        user_id: userId!,
        event_type: 'clock_out',
      });
      toast({
        title: 'Salida registrada',
        description: `Has fichado tu salida a las ${format(new Date(), 'HH:mm')}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleStartBreak = async () => {
    if (!selectedBreakReason) {
      toast({
        title: 'Selecciona un motivo',
        description: 'Debes seleccionar el motivo de la pausa',
        variant: 'destructive',
      });
      return;
    }

    try {
      await clockAction.mutateAsync({
        user_id: userId!,
        event_type: 'break_start',
        break_reason: selectedBreakReason,
        notes: breakNotes || undefined,
      });
      toast({
        title: 'Pausa iniciada',
        description: `Has iniciado una pausa: ${getBreakReasonConfig(selectedBreakReason)?.label}`,
      });
      setBreakDialogOpen(false);
      setSelectedBreakReason(null);
      setBreakNotes('');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleEndBreak = async () => {
    try {
      await clockAction.mutateAsync({
        user_id: userId!,
        event_type: 'break_end',
      });
      toast({
        title: 'Pausa finalizada',
        description: 'Has retomado tu jornada laboral',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentBreakConfig = status?.current_break_reason
    ? getBreakReasonConfig(status.current_break_reason)
    : null;

  return (
    <>
      <Card className="glass-card overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Control de Fichaje
            </CardTitle>
            <Badge className={cn('text-sm', getStatusColor(status?.current_status || 'clocked_out'))}>
              {getStatusLabel(status?.current_status || 'clocked_out')}
            </Badge>
          </div>
          {status?.shift_name && (
            <p className="text-sm text-muted-foreground mt-1">
              Turno: {status.shift_name} ({status.shift_start} - {status.shift_end})
            </p>
          )}
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {/* Current Time Display */}
          <div className="text-center">
            <p className="text-5xl font-bold font-mono bg-gradient-magma bg-clip-text text-transparent">
              {format(new Date(), 'HH:mm:ss')}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {format(new Date(), "EEEE, d 'de' MMMM", { locale: es })}
            </p>
          </div>

          {/* Today's Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <Timer className="w-4 h-4" />
                <span className="text-sm font-medium">Tiempo Trabajado</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                {formatMinutesToTime(status?.today_worked_minutes || 0)}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-2 text-yellow-600 mb-1">
                <Coffee className="w-4 h-4" />
                <span className="text-sm font-medium">Tiempo Pausa</span>
              </div>
              <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                {formatMinutesToTime(status?.today_break_minutes || 0)}
              </p>
            </div>
          </div>

          {/* Current Break Info */}
          {status?.current_status === 'on_break' && currentBreakConfig && (
            <div className="p-4 rounded-xl border-2 border-dashed border-yellow-400 bg-yellow-50 dark:bg-yellow-950/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${currentBreakConfig.color}20` }}
                  >
                    {BREAK_ICONS[currentBreakConfig.icon] &&
                      (() => {
                        const IconComponent = BREAK_ICONS[currentBreakConfig.icon];
                        return <IconComponent className="w-5 h-5" style={{ color: currentBreakConfig.color }} />;
                      })()}
                  </div>
                  <div>
                    <p className="font-medium">{currentBreakConfig.label}</p>
                    <p className="text-sm text-muted-foreground">
                      {currentBreakConfig.is_paid ? 'Remunerada' : 'No remunerada'}
                    </p>
                  </div>
                </div>
                <Badge variant={currentBreakConfig.is_paid ? 'default' : 'secondary'}>
                  En curso
                </Badge>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {status?.current_status === 'clocked_out' && (
              <Button
                size="lg"
                className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                onClick={handleClockIn}
                disabled={clockAction.isPending}
              >
                <LogIn className="w-5 h-5 mr-2" />
                Fichar Entrada
              </Button>
            )}

            {status?.current_status === 'working' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                    onClick={() => setBreakDialogOpen(true)}
                    disabled={clockAction.isPending}
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Iniciar Pausa
                  </Button>
                  <Button
                    size="lg"
                    className="h-12 bg-red-600 hover:bg-red-700"
                    onClick={handleClockOut}
                    disabled={clockAction.isPending}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Fichar Salida
                  </Button>
                </div>
              </>
            )}

            {status?.current_status === 'on_break' && (
              <Button
                size="lg"
                className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                onClick={handleEndBreak}
                disabled={clockAction.isPending}
              >
                <Play className="w-5 h-5 mr-2" />
                Finalizar Pausa
              </Button>
            )}
          </div>

          {/* Today's Timeline */}
          {todayEntries && todayEntries.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Registro de hoy
              </p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {todayEntries.map((entry) => {
                  const breakConfig = entry.break_reason
                    ? getBreakReasonConfig(entry.break_reason)
                    : null;

                  return (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <Badge className={cn('text-xs', getEventTypeColor(entry.event_type))}>
                          {getEventTypeLabel(entry.event_type)}
                        </Badge>
                        {breakConfig && (
                          <span className="text-muted-foreground">
                            ({breakConfig.label})
                          </span>
                        )}
                      </div>
                      <span className="font-mono text-muted-foreground">
                        {format(new Date(entry.event_time), 'HH:mm')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Break Selection Dialog */}
      <Dialog open={breakDialogOpen} onOpenChange={setBreakDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coffee className="w-5 h-5" />
              Seleccionar Tipo de Pausa
            </DialogTitle>
            <DialogDescription>
              Selecciona el motivo de tu pausa conforme a la normativa laboral UE
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 py-4">
            {BREAK_REASONS.map((reason) => {
              const IconComponent = BREAK_ICONS[reason.icon] || MoreHorizontal;
              const isSelected = selectedBreakReason === reason.code;

              return (
                <button
                  key={reason.code}
                  onClick={() => setSelectedBreakReason(reason.code)}
                  className={cn(
                    'p-4 rounded-xl border-2 text-left transition-all hover:shadow-md',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${reason.color}20` }}
                    >
                      <IconComponent
                        className="w-5 h-5"
                        style={{ color: reason.color }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{reason.label}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {reason.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant={reason.is_paid ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {reason.is_paid ? 'Remunerada' : 'No remunerada'}
                        </Badge>
                        {reason.max_duration_minutes && (
                          <span className="text-xs text-muted-foreground">
                            máx. {reason.max_duration_minutes}min
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-primary" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="space-y-2">
            <Label htmlFor="break-notes">Notas (opcional)</Label>
            <Textarea
              id="break-notes"
              placeholder="Añade notas adicionales si es necesario..."
              value={breakNotes}
              onChange={(e) => setBreakNotes(e.target.value)}
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBreakDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleStartBreak}
              disabled={!selectedBreakReason || clockAction.isPending}
            >
              <Pause className="w-4 h-4 mr-2" />
              Iniciar Pausa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TimeClockWidget;
